import React from 'react';
import { getFormattedBuildDate } from '../../buildInfo';
import ThemeToggle from './ThemeToggle';
import NotificationCenter from './NotificationCenter';
import './TopBar.css';
import {
  Menu, X, MapPin, RefreshCw, Lightbulb,
  User as UserIcon, LogOut, PanelLeftClose, PanelLeftOpen,
} from 'lucide-react';

/**
 * Header — TopBar minimalny, spójny z Home.
 * Bez daty, godziny, event-badge'y i przycisku „nowości".
 * Zachowuje tylko: brand + lokalizacja + akcje utility + user + wyloguj.
 */
const Header = ({
  user,
  location,
  locationName,
  logoUrl,
  onLogout,
  onSidebarToggle,
  isSidebarOpen,
  isMobile,
  onRefreshData,
  isRefreshing,
  onSuggestionClick,
  communicatorUnread = 0,
  remindersCount = 0,
  resolutions = [],
  inquiries = [],
  lastSeenResolutionsKey = null,
  lastSeenInquiriesKey = null,
  onResolutionsSeen,
  setActiveTab,
  onMobileMenuToggle,
  isMobileMenuOpen = false,
}) => {
  const displayName = user?.firstName || user?.name || user?.username || '';
  const initials = (displayName || '?')
    .split(/[_\s]+/)
    .map((s) => s[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const userRole = user?.role === 'admin' ? 'Admin' : (user?.role || 'Pracownik');

  return (
    <header className="app-header topbar-luxe" data-testid="topbar-container">
      {/* Left group */}
      <div className="header-section header-brand">
        {!isMobile ? (
          <button
            className="topbar-icon-btn topbar-sidebar-toggle"
            onClick={onSidebarToggle}
            aria-label={isSidebarOpen ? 'Ukryj menu boczne' : 'Pokaż menu boczne'}
            title={isSidebarOpen ? 'Ukryj menu boczne' : 'Pokaż menu boczne'}
            data-testid="topbar-sidebar-toggle"
          >
            {isSidebarOpen ? <PanelLeftClose size={20} /> : <PanelLeftOpen size={20} />}
          </button>
        ) : (
          <button
            type="button"
            className="topbar-icon-btn"
            onClick={() => onMobileMenuToggle && onMobileMenuToggle(!isMobileMenuOpen)}
            aria-label="Otwórz menu"
            data-testid="header-mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        )}

        <img src={logoUrl} alt="MR JERRY" className="header-logo" data-testid="topbar-logo" />
        <div className="header-brand-info">
          <span className="header-title">MR JERRY</span>
        </div>
      </div>

      {/* Center — location badge stands alone, visually separated */}
      <div className="header-section header-center">
        <span className="topbar-location-pill topbar-location-pill--center" data-testid="topbar-location-badge">
          <MapPin size={13} strokeWidth={2.2} />
          {locationName}
        </span>
      </div>

      {/* Right group */}
      <div className="header-section header-actions">
        <button
          className={`topbar-icon-btn ${isRefreshing ? 'refreshing' : ''}`}
          onClick={onRefreshData}
          title="Odśwież dane"
          aria-label="Odśwież dane"
          disabled={isRefreshing}
          data-testid="topbar-refresh"
        >
          <RefreshCw size={18} strokeWidth={1.8} />
        </button>

        <button
          className="topbar-icon-btn"
          onClick={onSuggestionClick}
          title="Zgłoś pomysł na ulepszenie"
          aria-label="Zgłoś pomysł"
          data-testid="topbar-suggestion"
        >
          <Lightbulb size={18} strokeWidth={1.8} />
        </button>

        <ThemeToggle compact />

        <div data-testid="topbar-notifications">
          <NotificationCenter
            communicatorUnread={communicatorUnread}
            remindersCount={remindersCount}
            resolutions={resolutions}
            inquiries={inquiries}
            lastSeenResolutionsKey={lastSeenResolutionsKey}
            lastSeenInquiriesKey={lastSeenInquiriesKey}
            onResolutionsSeen={onResolutionsSeen}
            setActiveTab={setActiveTab}
          />
        </div>

        <div className="topbar-user-pill" data-testid="topbar-user-menu">
          <div className="topbar-avatar" aria-hidden>{initials || <UserIcon size={14} />}</div>
          <div className="topbar-user-text">
            <span className="topbar-user-name">{displayName}</span>
            <span className="topbar-user-role">{userRole}</span>
          </div>
        </div>

        <button
          className="topbar-logout-btn"
          onClick={onLogout}
          data-testid="logout-button"
          title="Wyloguj"
        >
          <LogOut size={15} strokeWidth={2} />
          <span>Wyloguj</span>
        </button>
      </div>

      {/* Hidden — retained for backwards compatibility with build info fetch */}
      <span data-testid="topbar-build-info" style={{ display: 'none' }}>
        {getFormattedBuildDate()}
      </span>
    </header>
  );
};

export default Header;
